import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import {
  FaClipboardList,
  FaUsers,
  FaTruck,
  FaCheckCircle,
  FaRegQuestionCircle,
  FaChevronDown,
  FaInstagram,
  FaLinkedinIn,
  FaPaperPlane,
  FaArrowRight,
} from 'react-icons/fa';
import { CiMenuBurger } from 'react-icons/ci';
import { MdAddBusiness } from 'react-icons/md';
import { RiFileUserFill } from 'react-icons/ri';
import TatManagerLogo from '../assets/images/TAT MANAGER LOGO.svg';

const Landing = () => {
  const navigate = useNavigate();
  const sectionIds = useRef(['inicio', 'problemas', 'modulos', 'como-funciona', 'beneficios', 'faq', 'beta']);
  const [activeSection, setActiveSection] = useState('inicio');
  const [isLeaving, setIsLeaving] = useState(false);
  const leaveTimerRef = useRef(0);
  const [betaStatus, setBetaStatus] = useState({ type: 'idle', message: '' });
  const [betaSending, setBetaSending] = useState(false);
  const [betaSubmitted, setBetaSubmitted] = useState(false);
  const [betaSubmitMode, setBetaSubmitMode] = useState('');
  const [betaData, setBetaData] = useState({
    name: '',
    workshop: '',
    email: '',
    consent: false,
  });

  useEffect(() => {
    const root = document.getElementById('root');
    const app = document.querySelector('.App');
    root?.classList.add('root--public');
    app?.classList.add('App--public');

    return () => {
      root?.classList.remove('root--public');
      app?.classList.remove('App--public');
    };
  }, []);

  const reduceMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) window.clearTimeout(leaveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const ids = sectionIds.current;
    const root = document.getElementById('landing-main');

    if (!ids?.length) return undefined;

    const headerOffsetPx = 108;
    let rafId = 0;

    const computeActive = () => {
      rafId = 0;

      const isRootScrollable = (() => {
        if (!root) return false;
        const style = window.getComputedStyle(root);
        const overflowY = style?.overflowY || '';
        const canScroll = overflowY === 'auto' || overflowY === 'scroll';
        return canScroll && root.scrollHeight > root.clientHeight + 2;
      })();

      // If we're at the very bottom, force the last section (fixes short end sections like FAQ/Beta)
      if (isRootScrollable) {
        const remaining = root.scrollHeight - root.scrollTop - root.clientHeight;
        if (remaining <= 2) {
          setActiveSection(ids[ids.length - 1]);
          return;
        }
      } else {
        const doc = document.documentElement;
        const remaining = doc.scrollHeight - (window.scrollY + window.innerHeight);
        if (remaining <= 2) {
          setActiveSection(ids[ids.length - 1]);
          return;
        }
      }

      let bestId = ids[0];
      let bestTop = -Infinity;

      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const rect = el.getBoundingClientRect();

        // Prefer the last section whose top is above the sticky header
        if (rect.top <= headerOffsetPx && rect.top > bestTop) {
          bestTop = rect.top;
          bestId = id;
        }
      });

      setActiveSection(bestId);
    };

    const schedule = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(computeActive);
    };

    // Listen on both: depending on layout, scrolling can happen in the internal container or in the window
    root?.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();

    return () => {
      root?.removeEventListener('scroll', schedule);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;

    const nodes = Array.from(document.querySelectorAll('[data-reveal]'));
    if (nodes.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [reduceMotion]);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (!section) return;

    section.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  };

  const navigateWithLeave = (to) => {
    if (reduceMotion) {
      navigate(to);
      return;
    }

    setIsLeaving(true);
    if (leaveTimerRef.current) window.clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = window.setTimeout(() => {
      navigate(to);
    }, 180);
  };

  const goToLogin = () => navigateWithLeave('/login');
  const goToRegister = () => navigateWithLeave('/login?mode=register');

  const navItems = useMemo(
    () => [
      { id: 'problemas', label: 'Problemas' },
      { id: 'modulos', label: 'Módulos' },
      { id: 'como-funciona', label: 'Cómo funciona' },
      { id: 'beneficios', label: 'Beneficios' },
      { id: 'faq', label: 'Preguntas' },
      { id: 'beta', label: 'Beta' },
    ],
    []
  );

  const handleBetaChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBetaData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleBetaSubmit = async (e) => {
    e.preventDefault();
    setBetaStatus({ type: 'idle', message: '' });
    setBetaSubmitted(false);
    setBetaSubmitMode('');

    if (!betaData.consent) {
      setBetaStatus({
        type: 'error',
        message: 'Necesitamos tu autorización para registrar tus datos de prueba beta.',
      });
      return;
    }

    const payload = {
      ...betaData,
      submittedAt: new Date().toISOString(),
      source: 'landing',
    };

    try {
      const current = JSON.parse(localStorage.getItem('tat_beta_leads') || '[]');
      localStorage.setItem('tat_beta_leads', JSON.stringify([payload, ...current].slice(0, 50)));
    } catch {
      // ignore localStorage errors
    }

    const formatted = [
      'Solicitud Beta – TAT Manager',
      '',
      `Nombre: ${betaData.name}`,
      `Correo: ${betaData.email}`,
      `Taller: ${betaData.workshop || '-'}`,
      `Fecha: ${new Date().toLocaleString()}`,
      '',
    ].join('\n');

    const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    const canSendEmail = !!(EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY);

    if (canSendEmail) {
      try {
        setBetaSending(true);

        const nowText = new Date().toLocaleString();

        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            title: 'Solicitud Beta – TAT Manager',
            name: betaData.name,
            email: betaData.email,
            time: nowText,
            message: [
              formatted,
              '---',
              `submitted_at: ${payload.submittedAt}`,
              `source: ${payload.source}`,
            ].join('\n'),
          },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );

        setBetaStatus({
          type: 'success',
          message: '¡Listo! Recibimos tu solicitud. Te contactaremos cuando abramos la beta.',
        });
        setBetaSubmitMode('sent');
        setBetaSubmitted(true);
        setBetaData({ name: '', workshop: '', email: '', consent: false });
        return;
      } catch {
        // Fall back below
      } finally {
        setBetaSending(false);
      }
    }

    try {
      await navigator.clipboard.writeText(formatted);
      setBetaStatus({
        type: 'success',
        message:
          'No se pudo enviar automáticamente. Copiamos tus datos al portapapeles como respaldo.',
      });
      setBetaSubmitMode('fallback');
      setBetaSubmitted(true);
    } catch {
      setBetaStatus({
        type: 'success',
        message:
          'No se pudo enviar automáticamente. Tus datos quedaron registrados localmente como respaldo.',
      });
      setBetaSubmitMode('fallback');
      setBetaSubmitted(true);
    }
  };

  return (
    <div className={`landing ${isLeaving ? 'is-leaving' : ''}`}>
      {!reduceMotion && <div className="landing-leaveOverlay" aria-hidden="true" />}
      <header className="landing-header" role="banner">
        <div className="landing-container landing-headerInner">
          <a
            className="landing-brand"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('inicio');
            }}
            aria-label="Ir al inicio de la landing"
          >
            <img className="landing-brandLogo" src={TatManagerLogo} alt="TAT Manager" loading="eager" />
            <span className="landing-brandText">
              <strong>TAT Manager</strong>
              <span className="landing-brandSubtitle">Gestión para talleres mecánicos</span>
            </span>
          </a>

          <nav className="landing-nav" aria-label="Navegación de la landing">
            {navItems.map((item) => (
              <a
                key={item.id}
                className={`landing-navLink ${activeSection === item.id ? 'is-active' : ''}`}
                href="/"
                aria-current={activeSection === item.id ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.id);
                }}
              >
                {item.label}
              </a>
            ))}

            <button className="landing-btn landing-btnSmall landing-btnPrimary landing-btnLogin" type="button" onClick={goToLogin}>
              Entrar <FaArrowRight aria-hidden="true" />
            </button>
          </nav>

          <div className="landing-mobileActions" aria-label="Acciones">
            <details className="landing-mobileMenu">
              <summary className="landing-mobileMenuSummary" aria-label="Abrir menú">
                <CiMenuBurger aria-hidden="true" />
                <span>Menú</span>
              </summary>
              <div className="landing-mobileMenuPanel" role="menu" aria-label="Secciones">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`landing-mobileMenuLink ${activeSection === item.id ? 'is-active' : ''}`}
                    role="menuitem"
                    onClick={(e) => {
                      scrollToSection(item.id);
                      e.currentTarget.closest('details')?.removeAttribute('open');
                    }}
                  >
                    {item.label}
                  </button>
                ))}
                <div className="landing-mobileMenuDivider" aria-hidden="true" />
                <button className="landing-btn landing-btnSmall landing-btnPrimary landing-btnLogin" type="button" onClick={goToLogin}>
                  Entrar <FaArrowRight aria-hidden="true" />
                </button>
                <button
                  className="landing-btn landing-btnSmall landing-btnSecondary"
                  type="button"
                  onClick={goToRegister}
                >
                  Crear cuenta
                </button>
              </div>
            </details>

            <button className="landing-btn landing-btnSmall landing-btnPrimary landing-btnLogin" type="button" onClick={goToLogin}>
              Entrar <FaArrowRight aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <main className="landing-main" id="landing-main" tabIndex={-1}>
        <section className="landing-hero" id="inicio" aria-label="Presentación" data-reveal>
          <div className="landing-container landing-heroGrid">
            <div className="landing-heroCopy">
              <p className="landing-eyebrow">Para dueños y administradores de talleres</p>
              <h1 className="landing-title">
                Ordena tu taller, da seguimiento a cada orden y mantén todo tu equipo alineado.
              </h1>
              <p className="landing-subtitle">
                TAT Manager centraliza talleres, órdenes, clientes, personal y proveedores en un solo lugar.
                Menos caos, más control y trazabilidad.
              </p>

              <div className="landing-heroCtas" aria-label="Acciones principales">
                <button className="landing-btn landing-btnPrimary landing-btnLogin" type="button" onClick={goToLogin}>
                  Entrar <FaArrowRight aria-hidden="true" />
                </button>
                <button className="landing-btn landing-btnSecondary" type="button" onClick={goToRegister}>
                  Crear cuenta
                </button>
              </div>

              <ul className="landing-heroBullets" aria-label="Beneficios clave">
                <li>
                  <FaCheckCircle aria-hidden="true" />
                  <span>Seguimiento claro de órdenes y estados</span>
                </li>
                <li>
                  <FaCheckCircle aria-hidden="true" />
                  <span>Información de clientes y vehículos ordenada</span>
                </li>
                <li>
                  <FaCheckCircle aria-hidden="true" />
                  <span>Operación más consistente entre personal y talleres</span>
                </li>
              </ul>
            </div>

            <div className="landing-heroVisual" aria-label="Vista previa ilustrativa">
              <div className="landing-mock" role="img" aria-label="Mock visual del panel de trabajo">
                <div className="landing-mockTop">
                  <span className="landing-mockDot" />
                  <span className="landing-mockDot" />
                  <span className="landing-mockDot" />
                  <span className="landing-mockTitle">Panel</span>
                </div>
                <div className="landing-mockBody">
                  <div className="landing-mockCard">
                    <p className="landing-mockLabel">Órdenes</p>
                    <p className="landing-mockValue">En seguimiento</p>
                  </div>
                  <div className="landing-mockCard">
                    <p className="landing-mockLabel">Clientes</p>
                    <p className="landing-mockValue">Historial a la mano</p>
                  </div>
                  <div className="landing-mockCard">
                    <p className="landing-mockLabel">Personal</p>
                    <p className="landing-mockValue">Roles y actividad</p>
                  </div>
                </div>
              </div>
              <p className="landing-visualNote">Visual ilustrativo (sin datos reales).</p>
            </div>
          </div>
        </section>

        <section className="landing-section" id="problemas" aria-label="Problemas" data-reveal>
          <div className="landing-container">
            <header className="landing-sectionHeader">
              <h2 className="landing-h2">Problemas</h2>
              <p className="landing-lead">
                Si hoy dependes de WhatsApp, libretas, Excel suelto o “lo tengo en la cabeza”, esto es para ti.
              </p>
            </header>

            <div className="landing-grid landing-grid3">
              <article className="landing-card">
                <h3 className="landing-h3">Órdenes sin seguimiento</h3>
                <p>Se pierden avances, faltan piezas, no hay responsables claros o se duplican tareas.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Información dispersa</h3>
                <p>Clientes, vehículos y trabajos anteriores quedan en distintos lugares y cuesta encontrarlos.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Estados poco claros</h3>
                <p>Sin una vista consistente de estados, el equipo y el cliente reciben mensajes diferentes.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Dependencia del “jefe”</h3>
                <p>Si el encargado no está, nadie sabe qué sigue. La operación se vuelve frágil.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Falta de orden con proveedores</h3>
                <p>Compras, entregas y contactos se vuelven un dolor cuando no están centralizados.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="landing-section" id="modulos" aria-label="Módulos" data-reveal>
          <div className="landing-container">
            <header className="landing-sectionHeader">
              <h2 className="landing-h2">Módulos</h2>
              <p className="landing-lead">Los mismos módulos que ya usas en la app, presentados de forma clara.</p>
            </header>

            <div className="landing-grid landing-grid5">
              <article className="landing-moduleCard">
                <div className="landing-moduleIcon" aria-hidden="true">
                  <MdAddBusiness />
                </div>
                <h3 className="landing-h3">Talleres</h3>
                <p>Organiza tu operación por taller y define el contexto de trabajo.</p>
              </article>

              <article className="landing-moduleCard">
                <div className="landing-moduleIcon" aria-hidden="true">
                  <FaClipboardList />
                </div>
                <h3 className="landing-h3">Órdenes</h3>
                <p>Crea, consulta y da seguimiento al avance con estados consistentes.</p>
              </article>

              <article className="landing-moduleCard">
                <div className="landing-moduleIcon" aria-hidden="true">
                  <FaUsers />
                </div>
                <h3 className="landing-h3">Clientes</h3>
                <p>Centraliza datos, historial y comunicación para atender mejor.</p>
              </article>

              <article className="landing-moduleCard">
                <div className="landing-moduleIcon" aria-hidden="true">
                  <RiFileUserFill />
                </div>
                <h3 className="landing-h3">Personal</h3>
                <p>Ten a la mano el directorio y la información del equipo.</p>
              </article>

              <article className="landing-moduleCard">
                <div className="landing-moduleIcon" aria-hidden="true">
                  <FaTruck />
                </div>
                <h3 className="landing-h3">Proveedores</h3>
                <p>Controla contactos y datos relevantes para compras y refacciones.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="landing-section" id="como-funciona" aria-label="Cómo funciona" data-reveal>
          <div className="landing-container">
            <header className="landing-sectionHeader">
              <h2 className="landing-h2">Cómo funciona</h2>
              <p className="landing-lead">Arranca en minutos con un flujo simple.</p>
            </header>

            <ol className="landing-steps" aria-label="Pasos">
              <li className="landing-step">
                <span className="landing-stepNum" aria-hidden="true">
                  1
                </span>
                <div className="landing-stepBody">
                  <h3 className="landing-h3">Inicia sesión</h3>
                  <p>Entra con tu cuenta y accede a tu entorno de trabajo.</p>
                </div>
              </li>
              <li className="landing-step">
                <span className="landing-stepNum" aria-hidden="true">
                  2
                </span>
                <div className="landing-stepBody">
                  <h3 className="landing-h3">Crea o elige el taller</h3>
                  <p>Define el taller con el que vas a operar y mantén el orden por contexto.</p>
                </div>
              </li>
              <li className="landing-step">
                <span className="landing-stepNum" aria-hidden="true">
                  3
                </span>
                <div className="landing-stepBody">
                  <h3 className="landing-h3">Administra órdenes, clientes y más</h3>
                  <p>Registra información y consulta el avance sin perder trazabilidad.</p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section className="landing-section landing-sectionAlt" id="beneficios" aria-label="Beneficios" data-reveal>
          <div className="landing-container">
            <header className="landing-sectionHeader">
              <h2 className="landing-h2">Beneficios enfocados en control y trazabilidad</h2>
              <p className="landing-lead">
                No es “más software”: es menos fricción operativa para que el taller avance.
              </p>
            </header>

            <div className="landing-grid landing-grid3">
              <article className="landing-card">
                <h3 className="landing-h3">Control operativo</h3>
                <p>Centraliza procesos para que el equipo trabaje con el mismo criterio.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Trazabilidad por orden</h3>
                <p>Consulta fácilmente qué se hizo, cuándo y en qué estado está cada orden.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Organización del cliente</h3>
                <p>Datos e historial disponibles cuando se necesitan, sin buscar en mil lugares.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Menos dependencia de “la persona clave”</h3>
                <p>La información queda en el sistema, no solo en la memoria de alguien.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Comunicación más consistente</h3>
                <p>Con estados claros, se reduce el “¿en qué va?” y mejora la coordinación.</p>
              </article>
              <article className="landing-card">
                <h3 className="landing-h3">Escalable para varios talleres</h3>
                <p>Crece con una estructura más ordenada por taller y por módulo.</p>
              </article>
            </div>

            <div className="landing-proof" aria-label="Cómo validarlo sin promesas">
              <h3 className="landing-h3">Cómo validarlo sin arriesgar tu operación</h3>
              <ul className="landing-proofList" aria-label="Sugerencias de prueba">
                <li>Arranca con 1 taller y 1 orden real (controlada).</li>
                <li>Define 3 estados y úsalos una semana para medir claridad.</li>
                <li>Centraliza cliente + vehículo + notas para evitar “búsquedas”.</li>
              </ul>
              <div className="landing-proofCtas" aria-label="Acciones de validación">
                <button className="landing-btn landing-btnSecondary" type="button" onClick={goToRegister}>
                  Crear cuenta y probar
                </button>
                <button className="landing-btn landing-btnGhost" type="button" onClick={() => scrollToSection('beta')}>
                  Prefiero beta guiada
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-section" id="faq" aria-label="Preguntas frecuentes" data-reveal>
          <div className="landing-container">
            <header className="landing-sectionHeader">
              <h2 className="landing-h2">Preguntas</h2>
              <p className="landing-lead">Respuestas cortas, sin promesas inventadas.</p>
            </header>

            <div className="landing-faq" role="list">
              <details className="landing-faqItem" role="listitem">
                <summary>
                  <FaRegQuestionCircle aria-hidden="true" />
                  ¿Necesito capacitación para empezar?
                  <FaChevronDown className="landing-faqChevron" aria-hidden="true" />
                </summary>
                <p>La idea es que el flujo sea simple. Si tu equipo ya usa órdenes y clientes, la adaptación es rápida.</p>
              </details>

              <details className="landing-faqItem" role="listitem">
                <summary>
                  <FaRegQuestionCircle aria-hidden="true" />
                  ¿Puedo manejar varios talleres?
                  <FaChevronDown className="landing-faqChevron" aria-hidden="true" />
                </summary>
                <p>Sí, el sistema está organizado por talleres y módulos para separar el contexto de operación.</p>
              </details>

              <details className="landing-faqItem" role="listitem">
                <summary>
                  <FaRegQuestionCircle aria-hidden="true" />
                  ¿Esto reemplaza Excel?
                  <FaChevronDown className="landing-faqChevron" aria-hidden="true" />
                </summary>
                <p>Puede reducir la dependencia de hojas sueltas al centralizar órdenes, clientes y proveedores.</p>
              </details>

              <details className="landing-faqItem" role="listitem">
                <summary>
                  <FaRegQuestionCircle aria-hidden="true" />
                  ¿Qué pasa si alguien se equivoca al capturar?
                  <FaChevronDown className="landing-faqChevron" aria-hidden="true" />
                </summary>
                <p>El sistema ayuda con estructura y consistencia. Para políticas de operación, tu proceso interno sigue siendo clave.</p>
              </details>

              <details className="landing-faqItem" role="listitem">
                <summary>
                  <FaRegQuestionCircle aria-hidden="true" />
                  ¿Hay un plan o precios?
                  <FaChevronDown className="landing-faqChevron" aria-hidden="true" />
                </summary>
                <p>Este repositorio no incluye información comercial. Si requieres acceso, usa “Crear cuenta” o coordina con tu administrador.</p>
              </details>

              <details className="landing-faqItem" role="listitem">
                <summary>
                  <FaRegQuestionCircle aria-hidden="true" />
                  ¿Puedo probar sin afectar mi operación?
                  <FaChevronDown className="landing-faqChevron" aria-hidden="true" />
                </summary>
                <p>Recomendación: prueba con un flujo controlado y valida el proceso antes de adoptar al 100%.</p>
              </details>

              <details className="landing-faqItem" role="listitem">
                <summary>
                  <FaRegQuestionCircle aria-hidden="true" />
                  ¿Qué necesito para entrar?
                  <FaChevronDown className="landing-faqChevron" aria-hidden="true" />
                </summary>
                <p>Una cuenta activa. Si aún no la tienes, usa “Crear cuenta” para registrarte.</p>
              </details>
            </div>
          </div>
        </section>

        <section className="landing-section landing-cta" aria-label="Llamado a la acción" data-reveal>
          <div className="landing-container landing-ctaInner">
            <h2 className="landing-h2">Empieza con una operación más ordenada</h2>
            <p className="landing-lead">
              Entra ahora o crea una cuenta para comenzar. Sin números inflados, solo un flujo más claro.
            </p>
            <div className="landing-ctaButtons">
              <button className="landing-btn landing-btnPrimary landing-btnLogin" type="button" onClick={goToLogin}>
                Entrar <FaArrowRight aria-hidden="true" />
              </button>
              <button className="landing-btn landing-btnSecondary" type="button" onClick={goToRegister}>
                Crear cuenta
              </button>
            </div>
          </div>
        </section>

        <section className="landing-section" id="beta" aria-label="Beta" data-reveal>
          <div className="landing-container">
            <header className="landing-sectionHeader">
              <h2 className="landing-h2">Beta</h2>
              <p className="landing-lead">
                Regístrate para la prueba beta. Solo pedimos nombre y correo. Al enviar, te mostraremos confirmación.
              </p>
            </header>

            {betaSubmitted ? (
              <div className="landing-contactCard landing-betaSuccess" role="status" aria-live="polite">
                <div className="landing-betaSuccessIcon" aria-hidden="true">
                  <FaPaperPlane />
                </div>
                <h3 className="landing-h3">¡Gracias! Recibimos tu solicitud</h3>
                <p className="landing-betaSuccessText">
                  {betaSubmitMode === 'sent'
                    ? 'Te contactaremos cuando abramos la beta. Revisa tu correo por si necesitas confirmar algo.'
                    : 'Registramos tu solicitud como respaldo. Si deseas, puedes enviarla de nuevo más tarde.'}
                </p>

                {betaStatus.type !== 'idle' && (
                  <p className="landing-formStatus is-success">{betaStatus.message}</p>
                )}

                <div className="landing-proofCtas" aria-label="Acciones">
                  <button
                    className="landing-btn landing-btnGhost"
                    type="button"
                    onClick={() => {
                      setBetaSubmitted(false);
                      setBetaSubmitMode('');
                      setBetaStatus({ type: 'idle', message: '' });
                    }}
                  >
                    Enviar otra solicitud
                  </button>
                  <button className="landing-btn landing-btnPrimary landing-btnLogin" type="button" onClick={goToLogin}>
                    Entrar <FaArrowRight aria-hidden="true" />
                  </button>
                </div>
              </div>
            ) : (
              <form className="landing-contactCard landing-betaForm" onSubmit={handleBetaSubmit} aria-label="Registro beta">
                <div className="landing-formGrid">
                  <label className="landing-field">
                    <span className="landing-label">Nombre completo *</span>
                    <input
                      className="landing-input"
                      name="name"
                      type="text"
                      value={betaData.name}
                      onChange={handleBetaChange}
                      required
                      autoComplete="name"
                    />
                  </label>

                  <label className="landing-field">
                    <span className="landing-label">Correo *</span>
                    <input
                      className="landing-input"
                      name="email"
                      type="email"
                      value={betaData.email}
                      onChange={handleBetaChange}
                      required
                      autoComplete="email"
                    />
                  </label>

                  <label className="landing-field">
                    <span className="landing-label">Taller (opcional)</span>
                    <input
                      className="landing-input"
                      name="workshop"
                      type="text"
                      value={betaData.workshop}
                      onChange={handleBetaChange}
                      autoComplete="organization"
                    />
                  </label>
                </div>

                <label className="landing-consent">
                  <input
                    name="consent"
                    type="checkbox"
                    checked={betaData.consent}
                    onChange={handleBetaChange}
                    required
                  />
                  <span>
                    Autorizo que mis datos se registren para fines de contacto de la prueba beta.
                  </span>
                </label>

                <div className="landing-contactButtons">
                  <button
                    className="landing-btn landing-btnPrimary"
                    type="submit"
                    disabled={betaSending || !betaData.consent}
                  >
                    {betaSending ? 'Enviando…' : 'Enviar solicitud beta'}
                  </button>
                  <button className="landing-btn landing-btnOutlinePrimary" type="button" onClick={goToLogin}>
                    Ya tengo cuenta → Entrar
                  </button>
                </div>

                <p className="landing-contactMicrocopy">
                  Te contactaremos cuando abramos la beta. No enviamos spam.
                </p>

                {betaStatus.type !== 'idle' && (
                  <p
                    className={`landing-formStatus ${betaStatus.type === 'error' ? 'is-error' : 'is-success'}`}
                    role={betaStatus.type === 'error' ? 'alert' : 'status'}
                  >
                    {betaStatus.message}
                  </p>
                )}

                <p className="landing-contactNote">
                  Nota: si el envío falla, guardamos el registro y te damos un respaldo para copiar.
                </p>
              </form>
            )}
          </div>
        </section>
      </main>

      <div className="landing-mobileCtaBar" aria-label="Acciones rápidas">
        <button className="landing-btn landing-btnSmall landing-btnOutlinePrimary" type="button" onClick={goToLogin}>
          Entrar <FaArrowRight aria-hidden="true" />
        </button>
        <button className="landing-btn landing-btnSmall landing-btnSecondary" type="button" onClick={goToRegister}>
          Crear cuenta
        </button>
      </div>

      <footer className="landing-footer" role="contentinfo">
        <div className="landing-container landing-footerInner">
          <div>
            <p className="landing-footerBrand">
              <strong>TAT Manager</strong>
              <span>Gestión para talleres mecánicos</span>
            </p>
            <p className="landing-footerSmall">© {new Date().getFullYear()} TAT Manager. Todos los derechos reservados.</p>
          </div>
          <div className="landing-footerLinks">
            <span className="landing-footerSocial" aria-label="Redes sociales (próximamente)">
              <span className="landing-footerSocialItem" aria-disabled="true" title="Instagram (próximamente)">
                <FaInstagram aria-hidden="true" />
                Instagram
              </span>
              <span className="landing-footerSocialItem" aria-disabled="true" title="LinkedIn (próximamente)">
                <FaLinkedinIn aria-hidden="true" />
                LinkedIn
              </span>
            </span>
            <a
              className="landing-footerLink landing-footerLinkPrimary"
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                goToLogin();
              }}
            >
              Entrar
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
